/**
 * Document Service
 * 
 * This service handles secure document upload, download, verification,
 * and management with healthcare compliance features.
 */

import apiClient from './apiClient';

class DocumentService {
    constructor() {
        this.baseURL = '/documents';
    }

    // ============================================================================
    // DOCUMENT UPLOAD AND MANAGEMENT
    // ============================================================================

    /**
     * Upload patient document
     */
    async uploadDocument(uploadData) {
        try {
            const formData = new FormData();
            formData.append('file', uploadData.file);
            formData.append('patient_id', uploadData.patient_id || uploadData.patientId);
            formData.append('document_type', uploadData.document_type || uploadData.documentType);
            
            if (uploadData.title) formData.append('title', uploadData.title);
            if (uploadData.category) formData.append('category', uploadData.category);

            const response = await apiClient.post(`${this.baseURL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: uploadData.onProgress || null
            });

            return {
                success: true,
                data: response.data,
                message: "Document uploaded successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to upload document",
                data: null
            };
        }
    }

    /**
     * Get patient documents with filtering
     */
    async getPatientDocuments(patientId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient/${patientId}`, { params });
            return {
                success: true,
                data: response.data.documents || response.data,
                total: response.data.total_count || 0,
                message: "Documents fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch documents",
                data: []
            };
        }
    }

    /**
     * Download document
     */
    async downloadDocument(documentId, justification = null) {
        try {
            const params = justification ? { justification } : {};
            const response = await apiClient.get(`${this.baseURL}/${documentId}/download`, {
                params,
                responseType: 'blob'
            });

            return {
                success: true,
                data: {
                    url: window.URL.createObjectURL(new Blob([response.data])),
                    blob: response.data
                },
                message: "Document downloaded successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to download document",
                data: null
            };
        }
    }
}

export default new DocumentService(); 